import { Marked } from 'marked';
import type { Token, Tokens } from 'marked';

/**
 * Safe markdown → HTML rendering shared by the worker (exports) and the web
 * app (preview). Raw HTML embedded in markdown is ALWAYS escaped — it is
 * rendered as visible text, never interpreted — so the output contains only
 * tags produced by the markdown renderer itself. The web app additionally
 * runs DOMPurify over the result as defence in depth.
 */

function escapeHtml(html: string): string {
  return html
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/** Decode the entities marked leaves intact in inline text (it never double-escapes them). */
function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi, (match, ent: string) => {
    const lower = ent.toLowerCase();
    if (lower.startsWith('#')) {
      const hex = lower.startsWith('#x');
      const code = parseInt(hex ? lower.slice(2) : lower.slice(1), hex ? 16 : 10);
      // Out-of-range references are left as written — fromCodePoint would throw
      // a RangeError and take the whole document's render down with it.
      if (!Number.isInteger(code) || code < 0 || code > 0x10ffff) return match;
      return String.fromCodePoint(code);
    }
    const named: Record<string, string> = {
      amp: '&',
      lt: '<',
      gt: '>',
      quot: '"',
      apos: "'",
      nbsp: ' ',
    };
    return named[lower] ?? match;
  });
}

/** Plain text of inline tokens — what a reader sees (inline markdown stripped, raw HTML visible as text). */
function inlinePlainText(tokens: Token[] | undefined): string {
  let out = '';
  for (const token of tokens ?? []) {
    switch (token.type) {
      case 'br':
        out += ' ';
        break;
      case 'image':
        out += (token as Tokens.Image).text;
        break;
      default: {
        const t = token as { tokens?: Token[]; text?: string };
        // Escape tokens carry the literal char; codespans/html keep their raw text.
        if (t.tokens && t.tokens.length > 0 && token.type !== 'codespan' && token.type !== 'html') {
          out += inlinePlainText(t.tokens);
        } else if (typeof t.text === 'string') {
          out += t.text;
        }
      }
    }
  }
  return decodeEntities(out);
}

/** Slug for heading ids: NFKD, lowercase, diacritics stripped, `[a-z0-9-]` only. */
export function headingSlug(text: string): string {
  const slug = text
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'section';
}

/**
 * Per-document heading id allocator. The renderer and `extractHeadings` share this
 * so the ids they produce can never drift: same slug, same de-dup order.
 */
class HeadingIds {
  private seen = new Map<string, number>();
  /** Ids already handed out — a heading whose own slug ends in `-2` must not
   *  steal the id the second "…" heading would get. */
  private used = new Set<string>();

  next(text: string): string {
    const slug = headingSlug(text);
    let count = (this.seen.get(slug) ?? 0) + 1;
    let id = `doc-${slug}${count > 1 ? `-${count}` : ''}`;
    while (this.used.has(id)) {
      count += 1;
      id = `doc-${slug}-${count}`;
    }
    this.seen.set(slug, count);
    this.used.add(id);
    return id;
  }
}

// The Marked instance is module-level, so this allocator is too. Parsing is synchronous
// (`async: false`), so resetting it at the start of every renderMarkdown() call is safe and
// guarantees de-duplication is scoped to a single document.
let headingIds = new HeadingIds();

const marked = new Marked({
  gfm: true,
  breaks: false,
  renderer: {
    html({ text }: { text: string }) {
      return escapeHtml(text);
    },
    heading({ tokens, depth }) {
      const id = headingIds.next(inlinePlainText(tokens));
      return `<h${depth} id="${id}">${this.parser.parseInline(tokens)}</h${depth}>\n`;
    },
    // Only allow safe link protocols; render anything else as plain text.
    link({ href, title, tokens }) {
      const label = this.parser.parseInline(tokens);
      const url = String(href ?? '');
      if (!/^(https?:|mailto:)/i.test(url)) {
        return label;
      }
      const t = title ? ` title="${escapeHtml(title)}"` : '';
      return `<a href="${escapeHtml(url)}"${t} rel="noopener noreferrer">${label}</a>`;
    },
    // Images take the same allowlist as links. Without this they bypass the
    // link() check entirely, and the HTML export embeds this output with no
    // further sanitiser in front of it.
    image({ href, title, text }) {
      const url = String(href ?? '');
      const alt = escapeHtml(String(text ?? ''));
      if (!/^https?:/i.test(url)) {
        return alt;
      }
      const t = title ? ` title="${escapeHtml(title)}"` : '';
      return `<img src="${escapeHtml(url)}" alt="${alt}"${t}>`;
    },
  },
});

/** Render markdown to HTML with raw HTML escaped, links restricted to http(s)/mailto and stable `doc-` heading ids. */
export function renderMarkdown(markdown: string): string {
  headingIds = new HeadingIds();
  return marked.parse(markdown, { async: false });
}

export type HeadingDepth = 1 | 2 | 3 | 4 | 5 | 6;

export interface MarkdownHeading {
  depth: HeadingDepth;
  text: string;
  id: string;
}

/**
 * Headings of a document in reading order, with the exact ids `renderMarkdown` emits.
 * Headings nested in blockquotes/lists are included — the renderer ids those too.
 */
export function extractHeadings(markdown: string): MarkdownHeading[] {
  const ids = new HeadingIds();
  const headings: MarkdownHeading[] = [];
  // walkTokens is pre-order, which matches the order the renderer visits headings.
  marked.walkTokens(marked.lexer(markdown), (token) => {
    if (token.type !== 'heading') return;
    const heading = token as Tokens.Heading;
    const text = inlinePlainText(heading.tokens);
    headings.push({ depth: heading.depth as HeadingDepth, text, id: ids.next(text) });
  });
  return headings;
}

export interface OutlineItem extends MarkdownHeading {
  /** Indent level relative to the shallowest heading in the outline (0-based). */
  level: number;
}

/**
 * Outline entries for a table of contents: headings up to `maxDepth`, or none at all
 * when the document has fewer than `minHeadings` of them (a TOC for two headings is noise).
 */
export function outlineItems(
  headings: MarkdownHeading[],
  { minHeadings = 3, maxDepth = 3 }: { minHeadings?: number; maxDepth?: number } = {},
): OutlineItem[] {
  const kept = headings.filter((h) => h.depth <= maxDepth);
  if (kept.length < minHeadings) return [];
  const shallowest = Math.min(...kept.map((h) => h.depth));
  return kept.map((h) => ({ ...h, level: h.depth - shallowest }));
}

export { escapeHtml };
export { compareVersionLabels, normaliseVersionLabel, parseDocumentVersion } from './document-version.js';
